import React, { useState, useEffect, useRef } from 'react';

const StatsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  // Updated stats based on user's data
  const stats = [
    { label: "Countries", value: 160, suffix: "+" },
    { label: "Languages", value: 100, suffix: "+" },
    { label: "YouTube Channels", value: 550, suffix: "+" },
    { label: "Subscribers", value: 200, suffix: "M+" },
    { label: "Total Views", value: 200, suffix: "B+" },
    { label: "Brand Projects", value: 300, suffix: "+" },
    { label: "IPs Developed", value: 400, suffix: "+" },
    { label: "1M+ Subs Channels", value: 60, suffix: "+" },
    { label: "100K+ Subs Channels", value: 150, suffix: "+" },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const CountUpAnimation = ({ end, suffix, duration = 2000 }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      if (!isVisible) return;

      let startTime;
      const animateCount = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = timestamp - startTime;
        const percentage = Math.min(progress / duration, 1);
        const currentCount = Math.floor(end * percentage);

        setCount(currentCount);

        if (percentage < 1) {
          requestAnimationFrame(animateCount);
        }
      };

      requestAnimationFrame(animateCount);
    }, [isVisible, end, duration]);

    return (
      <span>
        {count}
        {suffix}
      </span>
    );
  };

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-br from-[#0077cc] to-[#00c6d7] relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Our Impact in Numbers
          </h2>
          <p className="text-xl text-white/90">
            Leading Enterprise MCN on YouTube
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
          {stats.slice(0, 5).map((stat, index) => (
            <div
              key={index}
              className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all hover:scale-105"
              data-testid={`stat-${index}`}
            >
              <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                <CountUpAnimation end={stat.value} suffix={stat.suffix} />
              </div>
              <div className="text-sm md:text-base text-white/90 font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
        
        {/* Second Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto mt-6">
          {stats.slice(5).map((stat, index) => (
            <div
              key={index + 5}
              className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all hover:scale-105"
              data-testid={`stat-${index + 5}`}
            >
              <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                <CountUpAnimation end={stat.value} suffix={stat.suffix} />
              </div>
              <div className="text-sm md:text-base text-white/90 font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
